import React, { useEffect, useMemo, useState } from "react";
import OfficeHoursService from "./officeHoursService";
import OfficeService from "./OfficeService";
import "./OfficeScheduleManager.css";

const officeHoursService = new OfficeHoursService();
const officeService = new OfficeService();

const OfficeScheduleManager = () => {
  const [officeHours, setOfficeHours] = useState([]);
  const [offices, setOffices] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [officeFilter, setOfficeFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    officeId: "",
    dayOfWeek: "",
    date: "",
    openHour: "",
    closeHour: "",
    incraments: "",
  });

  const role = localStorage.getItem("role")?.replace("ROLE_", "") || "";
  const canManage = role === "ADMIN" || role === "MANAGER";

  useEffect(() => {
    if (!canManage) {
      setMessage("You do not have permission to manage office schedules.");
      return;
    }

    fetchInitialData();
  }, [canManage]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setMessage("");

      const [officeRows, scheduleRows] = await Promise.all([
        officeService.getAllOffices(),
        officeHoursService.getAllOfficeHours(),
      ]);

      setOffices(Array.isArray(officeRows) ? officeRows : []);
      setOfficeHours(Array.isArray(scheduleRows) ? scheduleRows : []);
    } catch (err) {
      console.error("Failed to load office schedule data:", err);
      setMessage("Failed to load office schedules.");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredSchedules = async () => {
    try {
      setLoading(true);
      setMessage("");

      const data = await officeHoursService.filterOfficeHours({
        officeId: officeFilter,
        date: dateFilter,
      });

      setOfficeHours(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to filter office schedules:", err);
      setMessage("Failed to filter office schedules.");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = async () => {
    setOfficeFilter("");
    setDateFilter("");
    setSearchQuery("");

    try {
      setLoading(true);
      setMessage("");

      const data = await officeHoursService.getAllOfficeHours();
      setOfficeHours(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to reload office schedules:", err);
      setMessage("Failed to reload office schedules.");
    } finally {
      setLoading(false);
    }
  };

  const getScheduleId = (row) => {
    return row.officeHoursId ?? row.office_hours_id ?? row.id;
  };

  const getOfficeId = (office) => {
    return office.officeId ?? office.office_id ?? office.id;
  };

  const getOfficeName = (officeId) => {
    const idNumber = Number(officeId);

    const found = offices.find((office) => Number(getOfficeId(office)) === idNumber);

    return found?.officeName || found?.office_name || `Office #${officeId}`;
  };

  const normalizeTime = (time) => {
    if (!time) return "";

    // Handles both "09:00" and "09:00:00"
    return String(time).slice(0, 5);
  };

  const formatTimeForDisplay = (time) => {
    if (!time) return "N/A";

    const cleaned = normalizeTime(time);
    const [hourString, minuteString] = cleaned.split(":");

    const hour = Number(hourString);
    const minute = Number(minuteString);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return cleaned;
    }

    const date = new Date();
    date.setHours(hour, minute, 0, 0);

    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "N/A";

    const parts = String(date).split("-");
    if (parts.length !== 3) return date;

    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  };

  const filteredOfficeHours = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();

    if (!search) {
      return officeHours;
    }

    return officeHours.filter((row) => {
      const officeName = getOfficeName(row.officeId).toLowerCase();
      const date = String(row.date || "").toLowerCase();
      const day = String(row.dayOfWeek || row.DayOfWeek || "").toLowerCase();
      const openHour = String(row.openHour || "").toLowerCase();
      const closeHour = String(row.closeHour || "").toLowerCase();
      const increments = String(row.incraments || "").toLowerCase();

      return (
        officeName.includes(search) ||
        date.includes(search) ||
        day.includes(search) ||
        openHour.includes(search) ||
        closeHour.includes(search) ||
        increments.includes(search)
      );
    });
  }, [officeHours, searchQuery, offices]);

  const startEdit = (row) => {
    const id = getScheduleId(row);

    setEditingId(id);
    setMessage("");

    setEditForm({
      officeId: row.officeId || "",
      dayOfWeek: row.dayOfWeek || row.DayOfWeek || "",
      date: row.date || "",
      openHour: normalizeTime(row.openHour),
      closeHour: normalizeTime(row.closeHour),
      incraments: row.incraments || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      officeId: "",
      dayOfWeek: "",
      date: "",
      openHour: "",
      closeHour: "",
      incraments: "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveEdit = async (id) => {
    const parsedOfficeId = Number(editForm.officeId);
    const parsedIncrements = Number(editForm.incraments);

    if (!parsedOfficeId || parsedOfficeId <= 0) {
      setMessage("Office is required.");
      return;
    }

    if (!editForm.dayOfWeek) {
      setMessage("Day of week is required.");
      return;
    }

    if (!editForm.date) {
      setMessage("Date is required.");
      return;
    }

    if (!editForm.openHour || !editForm.closeHour) {
      setMessage("Open hour and close hour are required.");
      return;
    }

    if (!parsedIncrements || parsedIncrements <= 0) {
      setMessage("Appointments per 30 mins must be greater than 0.");
      return;
    }

    const payload = {
      officeId: parsedOfficeId,
      dayOfWeek: editForm.dayOfWeek,
      date: editForm.date,
      openHour: editForm.openHour,
      closeHour: editForm.closeHour,
      incraments: parsedIncrements,
    };

    try {
      setLoading(true);
      setMessage("");

      const updated = await officeHoursService.updateOfficeHours(id, payload);

      setOfficeHours((prev) =>
        prev.map((row) => {
          const rowId = getScheduleId(row);

          if (rowId !== id) {
            return row;
          }

          return {
            ...row,
            ...updated,
            ...payload,
            officeHoursId: id,
          };
        })
      );

      cancelEdit();
      setMessage("Office schedule updated successfully.");
    } catch (err) {
      console.error("Failed to update office schedule:", err);
      setMessage("Failed to update office schedule.");
    } finally {
      setLoading(false);
    }
  };

  const deleteSchedule = async (row) => {
    const id = getScheduleId(row);

    if (!id) {
      setMessage("Could not find schedule ID.");
      return;
    }

    const officeName = getOfficeName(row.officeId);
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this office schedule?\n\n` +
        `Office: ${officeName}\n` +
        `Date: ${row.date}\n` +
        `Time: ${normalizeTime(row.openHour)} - ${normalizeTime(row.closeHour)}\n\n` +
        `This cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);
      setMessage("");

      const deleted = await officeHoursService.deleteOfficeHours(id);

      if (!deleted) {
        throw new Error("Delete request failed.");
      }

      setOfficeHours((prev) => prev.filter((existing) => getScheduleId(existing) !== id));

      if (editingId === id) {
        cancelEdit();
      }

      setMessage("Office schedule deleted successfully.");
    } catch (err) {
      console.error("Failed to delete office schedule:", err);
      setMessage("Failed to delete office schedule.");
    } finally {
      setLoading(false);
    }
  };

  if (!canManage) {
    return (
      <div className="os-manager-wrapper">
        <div className="os-manager-card">
          <h2>Office Schedule Manager</h2>
          <p className="os-error">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="os-manager-wrapper">
      <div className="os-manager-card">
        <div className="os-manager-header">
          <div>
            <h2>Office Schedule Manager</h2>
            <p>Search, filter, edit, and delete existing office schedule rows.</p>
          </div>

          <button
            type="button"
            className="os-refresh-button"
            onClick={fetchInitialData}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {message && <div className="os-message">{message}</div>}

        <div className="os-filter-panel">
          <div className="os-filter-group">
            <label>Search</label>
            <input
              type="text"
              className="os-search-input"
              placeholder="Search office, day, date, time..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="os-filter-group">
            <label>Office</label>
            <select
              className="os-filter-select"
              value={officeFilter}
              onChange={(e) => setOfficeFilter(e.target.value)}
            >
              <option value="">All Offices</option>
              {offices.map((office) => {
                const id = getOfficeId(office);
                const name = office.officeName || office.office_name || `Office #${id}`;

                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="os-filter-group">
            <label>Date</label>
            <input
              type="date"
              className="os-date-input"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </div>

          <div className="os-filter-actions">
            <button
              type="button"
              className="os-apply-button"
              onClick={fetchFilteredSchedules}
              disabled={loading}
            >
              Apply Filters
            </button>

            <button
              type="button"
              className="os-clear-button"
              onClick={clearFilters}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="os-count-row">
          Showing {filteredOfficeHours.length} of {officeHours.length}
        </div>

        {loading && <p className="os-loading">Loading...</p>}

        <div className="os-table-wrapper">
          <table className="os-table">
            <thead>
              <tr>
                <th>Office</th>
                <th>Date</th>
                <th>Day</th>
                <th>Open</th>
                <th>Close</th>
                <th>Appointments / 30 mins</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loading && filteredOfficeHours.length === 0 && (
                <tr>
                  <td colSpan="7" className="os-empty">
                    No office schedules found.
                  </td>
                </tr>
              )}

              {filteredOfficeHours.map((row) => {
                const id = getScheduleId(row);
                const isEditing = editingId === id;

                return (
                  <tr key={id}>
                    <td>
                      {isEditing ? (
                        <select
                          name="officeId"
                          className="os-edit-select"
                          value={editForm.officeId}
                          onChange={handleEditChange}
                        >
                          <option value="">Select Office</option>
                          {offices.map((office) => {
                            const officeId = getOfficeId(office);
                            const officeName =
                              office.officeName || office.office_name || `Office #${officeId}`;

                            return (
                              <option key={officeId} value={officeId}>
                                {officeName}
                              </option>
                            );
                          })}
                        </select>
                      ) : (
                        <span className="os-office-name">{getOfficeName(row.officeId)}</span>
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          type="date"
                          name="date"
                          className="os-edit-input"
                          value={editForm.date}
                          onChange={handleEditChange}
                        />
                      ) : (
                        formatDateForDisplay(row.date)
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <select
                          name="dayOfWeek"
                          className="os-edit-select"
                          value={editForm.dayOfWeek}
                          onChange={handleEditChange}
                        >
                          <option value="">Select Day</option>
                          <option value="Monday">Monday</option>
                          <option value="Tuesday">Tuesday</option>
                          <option value="Wednesday">Wednesday</option>
                          <option value="Thursday">Thursday</option>
                          <option value="Friday">Friday</option>
                          <option value="Saturday">Saturday</option>
                          <option value="Sunday">Sunday</option>
                        </select>
                      ) : (
                        row.dayOfWeek || row.DayOfWeek || "N/A"
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          type="time"
                          name="openHour"
                          className="os-edit-input"
                          value={editForm.openHour}
                          onChange={handleEditChange}
                        />
                      ) : (
                        formatTimeForDisplay(row.openHour)
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          type="time"
                          name="closeHour"
                          className="os-edit-input"
                          value={editForm.closeHour}
                          onChange={handleEditChange}
                        />
                      ) : (
                        formatTimeForDisplay(row.closeHour)
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          name="incraments"
                          className="os-edit-input"
                          value={editForm.incraments}
                          onChange={handleEditChange}
                          min="1"
                        />
                      ) : (
                        <span className="os-badge">{row.incraments}</span>
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <div className="os-action-group">
                          <button
                            type="button"
                            className="os-save-button"
                            onClick={() => saveEdit(id)}
                            disabled={loading}
                          >
                            Save
                          </button>

                          <button
                            type="button"
                            className="os-cancel-button"
                            onClick={cancelEdit}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="os-action-group">
                          <button
                            type="button"
                            className="os-edit-button"
                            onClick={() => startEdit(row)}
                            disabled={loading}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="os-delete-button"
                            onClick={() => deleteSchedule(row)}
                            disabled={loading}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="os-note">
          Use this page to manage existing office schedules. Use Register Office
          Schedule only when creating new bulk schedule rows.
        </p>
      </div>
    </div>
  );
};

export default OfficeScheduleManager;